import { useEffect, useState } from 'react'

/**
 * Cookie consent by category, kept in localStorage and broadcast as a
 * window event so every component that loads third-party code can react
 * without a context provider.
 *
 * Categories (essential storage is always on and is not a category):
 *   calendar   Calendly booking embed — sets Calendly's cookies
 *   analytics  Vercel Web Analytics — cookieless, but a visitor may still
 *              prefer not to be counted, so it is switchable too
 *
 * Stored value: JSON {calendar, analytics}. Older values 'all' and
 * 'essential' from the first banner are still understood.
 *
 *   getConsent() → null (no choice yet) | {calendar, analytics}
 */
const KEY = 'mlmind:consent'
const CHANGE = 'mlmind:consent-change'
const OPEN = 'mlmind:consent-open'

export const CATEGORIES = ['calendar', 'analytics']
export const ALL_ON = { calendar: true, analytics: true }
export const ALL_OFF = { calendar: false, analytics: false }

const normalise = (raw) => {
  if (raw === 'all') return { ...ALL_ON }
  if (raw === 'essential') return { ...ALL_OFF }
  try {
    const obj = JSON.parse(raw)
    if (!obj || typeof obj !== 'object') return null
    const out = {}
    CATEGORIES.forEach((c) => {
      out[c] = obj[c] === true
    })
    return out
  } catch (e) {
    return null
  }
}

export const getConsent = () => {
  try {
    const raw = window.localStorage.getItem(KEY)
    return raw ? normalise(raw) : null
  } catch (e) {
    return null
  }
}

export const setConsent = (value) => {
  const clean = {}
  CATEGORIES.forEach((c) => {
    clean[c] = value && value[c] === true
  })
  try {
    window.localStorage.setItem(KEY, JSON.stringify(clean))
  } catch (e) {
    // storage blocked — the choice lasts for this page only
  }
  window.dispatchEvent(new CustomEvent(CHANGE, { detail: clean }))
}

export const isAllowed = (category) => {
  const c = getConsent()
  return Boolean(c && c[category])
}

// "Cookie settings" links reopen the banner in its manage view.
export const openCookieSettings = () => window.dispatchEvent(new CustomEvent(OPEN))

export const useConsent = () => {
  const [consent, set] = useState(getConsent)
  useEffect(() => {
    const onChange = (e) => set(e.detail)
    window.addEventListener(CHANGE, onChange)
    return () => window.removeEventListener(CHANGE, onChange)
  }, [])
  return consent
}

export const onOpenRequest = (handler) => {
  window.addEventListener(OPEN, handler)
  return () => window.removeEventListener(OPEN, handler)
}
